import { Button } from "@omni/ui/components/Button/button.component";
import { attempt } from "@omni/utils/function.utils";
import { cn } from "@omni/utils/styles";
import { AxiosResponse } from "axios";
import { useEffect, useState } from "react";

import { useBFFProductService } from "@/api/hooks/productService.hook";
import { useReviewsContext } from "@/components/Product/Reviews/context/reviews.context";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import * as UseFeatureTracker from "@/services/tracking/trackers/feature/useFeature.tracker";
import { ReviewFeedbackModel } from "@/types/service/reviews/reviewFeedback.model";
import { getCookie, setCookie } from "@/utils/cookie";
import { CookieList } from "@/utils/enums";

import { ThumbDown } from "./components/thumbDown/thumbDown.component";
import { ThumbUp } from "./components/thumbUp/thumbUp.component";

type Feedback = "helpfull" | "unhelpfull" | "inappropriate";

const getLocalFeedback = () => {
    const reviewsFeedbackCookie = getCookie(CookieList.REVIEWS_FEEDBACK);

    if (!reviewsFeedbackCookie) return {};

    return attempt<Record<string, Feedback>>(
        () => JSON.parse(reviewsFeedbackCookie),
        {}
    );
};

const updateLocalFeedback = (reviewId: string, feedback: Feedback) => {
    const localFeedback = {
        ...getLocalFeedback(),
        [reviewId]: feedback
    };

    setCookie(CookieList.REVIEWS_FEEDBACK, JSON.stringify(localFeedback));

    return localFeedback;
};

type FeedbackProps = {
    reviewId: string;
    feedback: ReviewFeedbackModel;
};

export const Feedback = ({ reviewId, feedback }: FeedbackProps) => {
    const { t } = useGranite();

    const { sku, brand, category } = useReviewsContext();

    const [localFeedback, setLocalFeedback] = useState<Feedback | null>(null);

    const productService = useBFFProductService();

    const disabled =
        localFeedback === "helpfull" || localFeedback === "unhelpfull";

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        const localFeedback = getLocalFeedback()[reviewId];

        if (localFeedback) {
            setLocalFeedback(localFeedback);
        }
    }, []);

    const sendFeedback = async (feedback: Feedback) => {
        await productService.post<{}, AxiosResponse<{}, any>, {}>(
            "/reviews/feedback",
            {},
            {
                params: {
                    reviewId: reviewId,
                    feedback: feedback
                }
            }
        );
    };

    const handleOnFeedback = (feedback: Feedback) => {
        updateLocalFeedback(reviewId, feedback);
        setLocalFeedback(feedback);
        sendFeedback(feedback);

        if (feedback === "inappropriate") {
            UseFeatureTracker.track({
                type: "bazaarvoice",
                name: "report",
                sku: sku,
                brand: brand,
                categoryId: category
            });
        } else {
            UseFeatureTracker.track({
                type: "bazaarvoice",
                name: "helpfulness",
                vote: feedback === "helpfull" ? "positive" : "negative",
                sku: sku,
                brand: brand,
                categoryId: category
            });
        }
    };

    const handleOnPositiveFeedbackClick = () => {
        handleOnFeedback("helpfull");
    };

    const handleOnNegativeFeedbackClick = () => {
        handleOnFeedback("unhelpfull");
    };

    const handleOnInappropriateFeedbackClick = () => {
        handleOnFeedback("inappropriate");
    };

    return (
        <div className={cn("tw:flex tw:items-center tw:gap-4")}>
            <p className={cn("tw:mb-0! tw:min-w-fit")}>
                {t("product.reviews.review.feedback.helpful")}
            </p>
            <div className={cn("tw:flex tw:gap-2 tw:items-center")}>
                <button
                    type={"button"}
                    onClick={handleOnPositiveFeedbackClick}
                    disabled={disabled}
                >
                    <ThumbUp
                        className={cn("tw:text-2xl tw:text-primary")}
                        filled={localFeedback === "helpfull"}
                    />
                </button>
                (
                {feedback.positiveFeedbackCount +
                    (localFeedback === "helpfull" ? 1 : 0)}
                )
            </div>
            <div className={cn("tw:flex tw:gap-2 tw:items-center")}>
                <button
                    type={"button"}
                    onClick={handleOnNegativeFeedbackClick}
                    disabled={disabled}
                >
                    <ThumbDown
                        className={cn("tw:text-2xl tw:text-primary")}
                        filled={localFeedback === "unhelpfull"}
                    />
                </button>
                (
                {feedback.negativeFeedbackCount +
                    (localFeedback === "unhelpfull" ? 1 : 0)}
                )
            </div>
            <Button
                className={cn("tw:p-0")}
                color={"link"}
                onClick={handleOnInappropriateFeedbackClick}
                disabled={localFeedback === "inappropriate"}
            >
                {localFeedback !== "inappropriate" &&
                    t("product.reviews.review.feedback.inappropriate")}
                {localFeedback === "inappropriate" &&
                    t("product.reviews.review.feedback.notified")}
            </Button>
        </div>
    );
};
